import { getNetworkConfig, NetworkType } from "./network";

/**
 * A settlement token this portal offers.
 *
 * THE PORTAL OWNS THIS LIST. It is deliberately not fetched: the picker is a client concern, it
 * renders with no round trip and no loading state, and adding a token is a frontend change. The
 * backend's role is narrower — confirming whether a token the portal names is actually usable on
 * this deploy (see features/tokens/useTokenSupport).
 *
 * No `decimals` here on purpose. Decimals must come from the contract's registry or amounts get
 * mispriced, and this app never does decimals math anyway — the backend returns human-scaled
 * numbers. Keeping the field out means there is no second source of truth to drift.
 */
export interface TokenOption {
  symbol: string;
  /** Checksummed ERC-20 address. Canonical id: what we persist and send as `?token=`. */
  address: string;
  /**
   * Preselected for a session with no stored preference. Exactly one per network.
   *
   * Explicit rather than "whichever is first" so reordering the list for display cannot silently
   * change which token every new session settles in.
   */
  isDefault?: true;
}

/** Mirrors TOKEN_REGISTRY_BOOTSTRAP in BackEnd configs/token_registry_config.ts. */
export const TOKENS: Record<NetworkType, TokenOption[]> = {
  mainnet: [
    // VIP is the default on both networks.
    { symbol: "VIP", address: "0x6860c34db140DB7B589DaDA38859a1d3736bbE3F", isDefault: true },
    // Circle NATIVE USDC, not the older bridged USDC.e (0x2791Bca1…4174). This is the address the
    // Keyfob vendor signed a device record for, which is what settled the choice.
    { symbol: "USDC", address: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359" },
  ],
  testnet: [
    { symbol: "VIP", address: "0xd278B77D630cFC02d8855b7402fC254c7AF5F744", isDefault: true },
    { symbol: "USDC", address: "0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582" },
  ],
};

/** The first entry is the default selection. */
export function tokensFor(network: NetworkType | undefined): TokenOption[] {
  return TOKENS[network ?? "testnet"] ?? [];
}

function shortAddress(address: string): string {
  return address.length > 10
    ? `${address.slice(0, 6)}…${address.slice(-4)}`
    : address;
}

/** Never empty, so no amount renders unitless. */
export function tokenLabel(token: TokenOption | undefined): string {
  if (!token) return "";
  return token.symbol || shortAddress(token.address);
}

/** The `?token=` value, or undefined to let the backend use its default token. */
export function tokenParam(token: TokenOption | undefined): string | undefined {
  return token?.address;
}

export function sameToken(a: string | undefined, b: string | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

/**
 * The stored preference if it is still on offer, else the network's default.
 *
 * A stored token that is no longer listed falls back rather than pinning the UI to something this
 * build does not offer. undefined only when the portal lists none for this network.
 */
export function resolveToken(
  tokens: TokenOption[],
  storedAddress: string | undefined,
): TokenOption | undefined {
  if (tokens.length === 0) return undefined;
  return (
    tokens.find((t) => sameToken(t.address, storedAddress)) ??
    tokens.find((t) => t.isDefault) ??
    tokens[0]
  );
}

/**
 * Total of `rows` that belong to `token`. Rows are token-tagged amounts (a redeem or refund).
 *
 * Used where the total is subtracted from a single-token balance, so summing across tokens would
 * mix units — 100 USDC + 100 VIP is 200 of nothing.
 *
 * Both fallbacks COUNT the row, which is the conservative direction wherever this gates a
 * transfer: an unattributed amount reduces the available balance and blocks, rather than inflating
 * it and letting a bad request through.
 */
export function sumForToken(
  rows: { amount?: number; settlementToken?: string }[],
  token: string | undefined,
): number {
  return rows
    .filter(
      (row) =>
        !token || !row.settlementToken || sameToken(row.settlementToken, token),
    )
    .reduce((total, row) => total + (row.amount ?? 0), 0);
}

export function tokenHoldingsUrl(
  network: NetworkType | undefined,
  tokenAddress: string | undefined,
  holder: string | undefined,
): string {
  const { explorerUrl } = getNetworkConfig(network);
  return `${explorerUrl}/token/${tokenAddress ?? ""}?a=${holder ?? ""}`;
}
